import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useProperty } from "../lib/PropertyContext";
import PropertySwitcher from "../components/customer/PropertySwitcher";
import { confirmDialog } from "../lib/confirm";
import { BRAND_COLORS } from "../lib/bookingStatus";
import { deDateShortTime } from "../lib/dateFormat";

export default function CustomerFamily() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  // Feb 2026 · 8 Sep 2026 · User-Directive „Objekt-Umschalter global": auch
  // im Familien-Bereich Kontext-Sync. Familien-Zugänge sind zwar pro Kunde
  // (nicht pro Objekt), aber Kunden mit mehreren Objekten profitieren von
  // konsistenter Navigation.
  const { selected } = useProperty(); // eslint-disable-line no-unused-vars
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ email: "", name: "" });
  const [loading, setLoading] = useState(false);

  const isFamily = user?.role === "family";
  const de = lang === "de";

  const load = useCallback(async () => {
    try { const { data } = await api.get("/family"); setRows(data); }
    catch (e) { console.debug("family load skipped (role restriction):", e?.response?.status); }
  }, []);
  useEffect(() => { if (!isFamily) load(); }, [isFamily, load]);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/family", form);
      toast.success(de ? "Einladung freigeschaltet." : "Invitation added.");
      setForm({ email: "", name: "" });
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({
      title: de ? "Zugang entfernen?" : "Remove access?",
      description: de
        ? "Das Familienmitglied kann sich danach nicht mehr in Ihr Objekt einloggen."
        : "This family member will lose access to your property.",
      confirmText: de ? "Entfernen" : "Remove",
      cancelText: de ? "Abbrechen" : "Cancel",
      variant: "danger",
    }))) return;
    await api.delete(`/family/${id}`);
    await load();
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="customer-family">
      <div className="mb-10">
        <div className="label-caps mb-3">Weissblom</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          {de ? "Familien-Zugang" : "Family access"}
        </h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
          {de
            ? "Laden Sie Familienmitglieder mit eingeschränkten Rechten ein. Sie können Berichte lesen und mit dem Team schreiben — jedoch keine Abwesenheiten planen oder Zugänge verwalten."
            : "Invite family members with limited permissions. They can read reports and message the team — but cannot create bookings or manage access."}
        </p>
      </div>

      {/* Feb 2026 · 8 Sep 2026 · Objekt-Umschalter — dient hier vor allem
          der Navigations-Konsistenz. Familien-Zugänge selbst sind pro Kunde,
          nicht pro Objekt. */}
      <PropertySwitcher testidPrefix="family-property-switcher" />

      {isFamily ? (
        <div className="bg-white border border-dashed border-navy/20 py-16 px-6 text-center text-[13px] text-navy-dark/60">
          {de
            ? "Als Familienmitglied können Sie diesen Bereich nicht bearbeiten."
            : "As a family member you cannot manage this section."}
        </div>
      ) : (
        <>
          <form
            onSubmit={submit}
            className="bg-white border border-navy/10 p-6 grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
            data-testid="family-form"
          >
            <input
              data-testid="family-email-input"
              required
              type="email"
              placeholder={de ? "E-Mail des Familienmitglieds" : "Family member email"}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
            />
            <input
              data-testid="family-name-input"
              placeholder={de ? "Name (optional)" : "Name (optional)"}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
            />
            <button
              data-testid="family-submit-btn"
              type="submit"
              disabled={loading}
              className="bg-navy text-ivory py-3 text-[12px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-50"
              style={{ background: BRAND_COLORS.navy }}
            >
              {loading ? t.common.loading : de ? "Einladen" : "Invite"}
            </button>
          </form>

          <div className="bg-white border border-navy/10">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/10">
                  <th className="label-caps text-left px-6 py-4">{de ? "Name" : "Name"}</th>
                  <th className="label-caps text-left px-6 py-4">{de ? "E-Mail" : "Email"}</th>
                  <th className="label-caps text-left px-6 py-4">{de ? "Zuletzt gelesen" : "Last read"}</th>
                  <th className="label-caps text-left px-6 py-4">{de ? "Zuletzt geschrieben" : "Last message"}</th>
                  <th className="label-caps text-left px-6 py-4">{de ? "Status" : "Status"}</th>
                  <th className="label-caps text-left px-6 py-4">{de ? "Aktionen" : "Actions"}</th>
                </tr>
              </thead>
              <tbody data-testid="family-list">
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-navy/5 last:border-b-0" data-testid={`family-row-${r.email}`}>
                    <td className="px-6 py-4 text-navy font-medium">{r.name || "—"}</td>
                    <td className="px-6 py-4 text-navy-dark/70 text-[13px]">{r.email}</td>
                    <td className="px-6 py-4 text-[12px] text-navy-dark/70" data-testid={`family-last-read-${r.email}`}>
                      {r.last_read_updates_at
                        ? deDateShortTime(r.last_read_updates_at)
                        : "—"}
                    </td>
                    <td className="px-6 py-4 text-[12px] text-navy-dark/70" data-testid={`family-last-msg-${r.email}`}>
                      {r.last_message_at
                        ? deDateShortTime(r.last_message_at)
                        : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <span className="label-caps">
                        {r.has_signed_in ? (de ? "Aktiv" : "Active") : (de ? "Eingeladen" : "Invited")}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        data-testid={`family-remove-${r.email}`}
                        onClick={() => remove(r.id)}
                        className="text-[12px] tracking-widest uppercase text-navy-dark/70 hover:text-red-800 transition-colors"
                      >
                        {de ? "Entfernen" : "Remove"}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[13px] text-navy-dark/50">
                      {de
                        ? "Noch keine Familienmitglieder eingeladen."
                        : "No family members invited yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-6 bg-ivory/60 border border-gold/30 text-[13px] text-navy-dark/80 leading-relaxed" data-testid="family-permissions-note">
            <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>
              {de ? "Rechte-Übersicht" : "Permissions overview"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <div>✓ {de ? "Wochenberichte + Fotos ansehen" : "View weekly reports + photos"}</div>
              <div>✓ {de ? "Objekt-Handbuch lesen" : "Read the property handbook"}</div>
              <div>✓ {de ? "Mit dem Team chatten" : "Message the team"}</div>
              <div>✓ {de ? "Kalender & Abwesenheiten sehen" : "See calendar & absences"}</div>
              <div className="opacity-60">× {de ? "Abwesenheiten planen (nur Eigentümer)" : "Plan absences (owner only)"}</div>
              <div className="opacity-60">× {de ? "Familien-Zugänge verwalten" : "Manage family access"}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
